<template>
  <div class="row">
    <div class="col-md-8">
      <h3>{{ title }}</h3>
    </div>
    <div class="col-md-4 mt-3">
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th class="thead-arizonablue">Transfer plan</th>
        <th class="thead-arizonablue">Destination School</th>
        <th class="thead-arizonablue">Destination Program</th>
        <th class="thead-arizonablue">Date Added</th>
        <th class="thead-arizonablue">Actions</th>
      </tr>
    </thead>

    <tbody v-if="isLoadingPlans">
      <tr>
        <th scope="row" colspan="5">
          <div class="loader"></div>
        </th>
      </tr>
    </tbody>

    <tbody v-else class="table-white">
      <tr v-if="generatedPlans.length == 0">
        <th scope="row" colspan="5">No plans found!</th>
      </tr>

      <tr v-else v-for="plan in generatedPlans" :key="plan.name">
        <td>
          <router-link
            :to="{
              path: '/plan-details/' + encodeURIComponent(plan.name),
              query: { planType: planDetailsType }
            }"
          >
            {{ plan.name }}
          </router-link>
        </td>

        <td>
          {{ plan.destinationSchool.name }}
        </td>

        <td>
          {{ plan.destinationProgram.display_name }}
        </td>

        <td>
          {{ formatDate(getDateAdded(plan)) }}
        </td>
        <td>
          <button
            type="button"
            class="delete-btn"
            @click="askDelete(plan)"
          >
            Delete
          </button>
          </td>
      </tr>
    </tbody>
  </table>
  <!-- Delete confirmation modal -->
  <div
    v-if="pendingDeletePlan"
    class="delete-modal-overlay"
    @click.self="cancelDelete"
  >
    <div class="delete-modal">
      <div class="delete-icon">
        <i class="fas fa-trash-alt"></i>
      </div>

      <h3>Delete transfer plan?</h3>

      <p>
        Are you sure you want to delete
        <strong>{{ pendingDeletePlan.name }}</strong>?
      </p>

      <p class="delete-warning">
        This plan will no longer appear in My Plans.
      </p>

      <div class="delete-modal-actions">
        <button
          type="button"
          class="cancel-delete-btn"
          :disabled="isDeleting"
          @click="cancelDelete"
        >
          Cancel
        </button>

        <button
          type="button"
          class="confirm-delete-btn"
          :disabled="isDeleting"
          @click="confirmDelete"
        >
          {{ isDeleting ? "Deleting..." : "Delete Plan" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import SearchSelect from "@/components/SearchSelect.vue";
import store from "@/store";

export default defineComponent({
  name: "PlanListTable",
  props: {
    generatedPlans: {
      type: Array as () => ReadonlyArray<any>,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    isLoadingPlans: {
      type: Boolean,
      required: false,
    },
    planDetailsType: {
      type: String,
      required: true,
    },
  },

  data() {
  return {
    pendingDeletePlan: null as any,
    isDeleting: false,
  };
},

methods: {
  getDateAdded(plan: any) {
    return plan?.dateAdded || null;
  },

  formatDate(value: string | null | undefined) {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  },

  askDelete(plan: any) {
    this.pendingDeletePlan = plan;
  },

  cancelDelete() {
    if (this.isDeleting) return;

    this.pendingDeletePlan = null;
  },

  async confirmDelete() {
    if (!this.pendingDeletePlan) return;

    this.isDeleting = true;

    try {
      await store.methods.deleteUserFile(
        this.pendingDeletePlan.name
      );

      this.pendingDeletePlan = null;
    } catch (error) {
      console.error("Failed to delete plan:", error);
    } finally {
      this.isDeleting = false;
    }
  },

},
components: { SearchSelect },
});
</script>
<style>
.table-container {
  background-color: #edf1f4;
  border-radius: 5px;
}
.delete-btn {
  background: #AB0520;
  color: white;
  border: none;
  padding: 10px 28px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: 0.2s ease;
}

.delete-btn:hover {
  background: #8f041a;
}

.delete-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 35, 75, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.delete-modal {
  background: white;
  width: 420px;
  max-width: calc(100% - 40px);
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(12, 35, 75, 0.22);
  border-top: 5px solid #AB0520;
}

.delete-icon {
  width: 58px;
  height: 58px;
  margin: 0 auto 18px;
  border-radius: 50%;
  background: #fbeaec;
  color: #AB0520;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.delete-modal h3 {
  margin-bottom: 12px;
  color: #0C234B;
  font-weight: 700;
}

.delete-modal p {
  margin-bottom: 8px;
  color: #4a4a4a;
  line-height: 1.5;
}

.delete-warning {
  font-size: 13px;
  color: #6c757d !important;
}

.delete-modal-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 26px;
}

.cancel-delete-btn,
.confirm-delete-btn {
  min-width: 120px;
  padding: 10px 18px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
}

.cancel-delete-btn {
  background: white;
  border: 1px solid #0C234B;
  color: #0C234B;
}

.cancel-delete-btn:hover {
  background: #f2f5f9;
}

.confirm-delete-btn {
  background: #AB0520;
  border: 1px solid #AB0520;
  color: white;
}

.confirm-delete-btn:hover {
  background: #8f041a;
}

.cancel-delete-btn:disabled,
.confirm-delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
